import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BarChart3,
  ChevronDown,
  Compass,
  Filter,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  WithTooltip,
} from "@/shared/ui/core";
import {
  splitPageActions,
  usePageActionsState,
  usePageFilterToggleState,
  usePageInsightsToggleState,
  type PageAction,
} from "@/app/pageActions/PageActionsContext";
import {
  resolvePageActionIcon,
  resolvePageActionLabel,
  resolvePageActionTitle,
} from "@/app/pageActions/pageActionIcons";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";
import { cn } from "@/shared/lib/cn";

function isCreateAction(action: PageAction): boolean {
  return action.id === "create";
}

function ActionControl({
  action,
  className,
}: {
  action: PageAction;
  className?: string;
}) {
  const isQuick = action.isQuickLink;
  const variant =
    action.variant && action.variant !== "ghost"
      ? action.variant
      : isQuick
        ? "secondary"
        : (action.variant ?? "secondary");
  const isSecondaryDanger = action.danger && variant === "secondary";
  const icon = resolvePageActionIcon(
    action,
    cn("size-4 shrink-0", isSecondaryDanger && "text-danger-fg"),
  );
  const label = resolvePageActionLabel(action);
  const title = resolvePageActionTitle(action);
  const rightIcon = isQuick ? (
    <ArrowUpRight className="size-3 text-text-muted opacity-70" aria-hidden />
  ) : undefined;

  const secondaryDangerClass =
    isSecondaryDanger &&
    "text-danger-fg border-border hover:bg-danger-soft hover:border-danger/40 hover:text-danger-fg";

  if (action.to) {
    return (
      <Button
        asChild
        variant={variant}
        size="sm"
        disabled={action.disabled}
        className={cn(
          isQuick && "border-border/80 text-text-muted hover:text-text",
          secondaryDangerClass,
          className,
        )}
        leftIcon={icon}
        rightIcon={rightIcon}
      >
        <Link to={action.to} title={title} aria-label={title}>
          {label}
        </Link>
      </Button>
    );
  }
  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      disabled={action.disabled}
      className={cn(
        isQuick && "border-border/80 text-text-muted hover:text-text",
        secondaryDangerClass,
        className,
      )}
      title={title}
      aria-label={title}
      leftIcon={icon}
      rightIcon={rightIcon}
      onClick={action.onClick}
    >
      {label}
    </Button>
  );
}

/** Mobile FAB for list "Tạo mới" (`id: "create"`). */
function CreateFab({
  action,
  clearBottomNav,
}: {
  action: PageAction;
  clearBottomNav?: boolean;
}) {
  const title = resolvePageActionTitle(action);
  const className = cn(
    "keyboard-lift fixed z-[var(--z-sticky-cta)] flex size-14 items-center justify-center rounded-full",
    "bg-primary text-on-primary shadow-3",
    clearBottomNav
      ? "right-4 bottom-[calc(var(--layout-bottom-nav-height)+1rem+env(safe-area-inset-bottom)+var(--keyboard-inset,0px))]"
      : "right-4 bottom-[calc(1rem+env(safe-area-inset-bottom)+var(--keyboard-inset,0px))]",
    "transition-transform active:scale-95",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "lg:hidden",
  );

  if (action.to) {
    return (
      <Link
        to={action.to}
        aria-label={title}
        title={title}
        className={cn(className, action.disabled && "pointer-events-none opacity-50")}
      >
        <Plus className="size-6" strokeWidth={2} absoluteStrokeWidth aria-hidden />
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      disabled={action.disabled}
      className={className}
      onClick={action.onClick}
    >
      <Plus className="size-6" strokeWidth={2} absoluteStrokeWidth aria-hidden />
    </button>
  );
}

/**
 * Insights + filter toggles and page actions for the Ops breadcrumb row.
 * Mobile: create (`id: "create"`) → FAB; back → Ops topbar; other actions → ⋯.
 * Icons/labels resolved centrally via `pageActionIcons` (id + iconKey).
 */
export type PageActionsToolbarProps = {
  className?: string;
  /** Lift FAB above Field/Exe bottom navigation. */
  clearBottomNav?: boolean;
  /** Suppress filter button in toolbar (e.g. when rendered in header). */
  hideFilterToggle?: boolean;
};

export function PageActionsToolbar({
  className,
  clearBottomNav,
  hideFilterToggle = false,
}: PageActionsToolbarProps) {
  const actions = usePageActionsState();
  const rawFilterToggle = usePageFilterToggleState();
  const filterToggle = hideFilterToggle ? null : rawFilterToggle;
  const insightsToggle = usePageInsightsToggleState();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const createAction = actions.find(isCreateAction);
  /** List/back nav lives in breadcrumb (and mobile topbar ←). */
  const withoutNavBack = actions.filter((a) => a.id !== "back" && a.id !== "list");
  const toolbarActions =
    !isDesktop && createAction
      ? withoutNavBack.filter((a) => !isCreateAction(a))
      : withoutNavBack;

  /**
   * MX3: phone promotes 1–2 primary CTAs outside ⋯ (same split as desktop D-DT4).
   * Create still goes to FAB on phone.
   */
  const quickLinks = toolbarActions.filter((a) => Boolean(a.isQuickLink));
  const standardActions = toolbarActions.filter((a) => !a.isQuickLink);

  const { inline, menu } = splitPageActions(standardActions);
  const firstDangerIndex = menu.findIndex((item) => item.danger);

  const showToolbar =
    toolbarActions.length > 0 || Boolean(filterToggle) || Boolean(insightsToggle);
  const showFab = !isDesktop && Boolean(createAction);

  if (!showToolbar && !showFab) return null;

  const filterLabel = filterToggle?.open ? "Ẩn bộ lọc" : "Hiện bộ lọc";
  const activeCount = filterToggle?.activeCount ?? 0;
  const insightsLabel = insightsToggle?.open ? "Ẩn tóm tắt" : "Hiện tóm tắt";
  const alertCount = insightsToggle?.alertCount ?? 0;
  const effectiveInsightsCount =
    insightsToggle?.count !== undefined
      ? insightsToggle.count
      : alertCount;
  const isPrimaryInsightsTone =
    insightsToggle?.badgeTone === "primary" ||
    (insightsToggle?.count !== undefined && insightsToggle?.badgeTone !== "danger");

  const hasViewControls = Boolean(insightsToggle || filterToggle);
  const hasQuickLinks = quickLinks.length > 0;
  const hasStandardActions = inline.length > 0 || menu.length > 0;

  return (
    <>
      {showToolbar ? (
        <div
          className={cn(
            "relative flex shrink-0 flex-wrap items-center justify-end gap-2",
            className,
          )}
        >
          {insightsToggle ? (
            <Button
              type="button"
              variant={insightsToggle.open ? "soft" : "secondary"}
              size="sm"
              onClick={insightsToggle.onToggle}
              aria-pressed={insightsToggle.open}
              aria-label={insightsLabel}
              title={insightsLabel}
              className="gap-1.5"
            >
              <BarChart3 className="size-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">Tóm tắt</span>
              {effectiveInsightsCount > 0 ? (
                <span
                  className={cn(
                    "inline-flex min-w-5 items-center justify-center rounded-full px-1.5",
                    "text-[11px] font-bold tabular-nums",
                    isPrimaryInsightsTone
                      ? insightsToggle.open
                        ? "bg-primary text-on-primary"
                        : "bg-primary-soft text-primary"
                      : insightsToggle.open
                        ? "bg-danger text-on-primary"
                        : "bg-danger-soft text-danger-fg",
                  )}
                >
                  {effectiveInsightsCount}
                </span>
              ) : null}
            </Button>
          ) : null}

          {filterToggle ? (
            <Button
              type="button"
              variant={filterToggle.open ? "soft" : "secondary"}
              size="sm"
              onClick={filterToggle.onToggle}
              aria-pressed={filterToggle.open}
              aria-label={filterLabel}
              title={filterLabel}
              className="gap-1.5"
            >
              <Filter className="size-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">Bộ lọc</span>
              {activeCount > 0 ? (
                <span
                  className={cn(
                    "inline-flex min-w-5 items-center justify-center rounded-full px-1.5",
                    "text-[11px] font-bold tabular-nums",
                    filterToggle.open
                      ? "bg-primary text-on-primary"
                      : "bg-bg text-text-muted",
                  )}
                >
                  {activeCount}
                </span>
              ) : null}
            </Button>
          ) : null}

          {/* Vách ngăn giữa View Controls và Quick Links / Actions */}
          {hasViewControls && (hasQuickLinks || hasStandardActions) ? (
            <div className="hidden h-4 w-px bg-border/80 sm:block mx-0.5 shrink-0" aria-hidden />
          ) : null}

          {/* Quick Navigation Links Dropdown hoặc Pills */}
          {hasQuickLinks ? (
            quickLinks.length > 1 ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1.5 border-border/80 text-text-muted hover:text-text bg-bg-surface hover:bg-bg-subtle"
                  >
                    <Compass className="size-4 shrink-0 text-text-muted" aria-hidden />
                    <span className="hidden sm:inline">Liên kết nhanh</span>
                    <ChevronDown className="size-3.5 opacity-60 shrink-0" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-1">
                  <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                    Chuyển đến màn hình
                  </div>
                  {quickLinks.map((link) => {
                    const icon = resolvePageActionIcon(link, "size-4 text-text-muted");
                    const label = resolvePageActionLabel(link);
                    const title = resolvePageActionTitle(link);
                    return link.to ? (
                      <DropdownMenuItem
                        key={link.id}
                        asChild
                        className="text-caption font-medium cursor-pointer"
                      >
                        <Link to={link.to} title={title} className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            {icon}
                            <span>{label}</span>
                          </span>
                          <ArrowUpRight className="size-3.5 text-text-muted opacity-70" />
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        key={link.id}
                        className="flex items-center justify-between gap-2 text-caption font-medium cursor-pointer"
                        onSelect={() => link.onClick?.()}
                      >
                        <span className="flex items-center gap-2">
                          {icon}
                          <span>{label}</span>
                        </span>
                        <ArrowUpRight className="size-3.5 text-text-muted opacity-70" />
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : quickLinks[0] ? (
              <ActionControl action={quickLinks[0]} />
            ) : null
          ) : null}

          {/* Vách ngăn giữa Quick Links và Standard Actions nếu có cả hai */}
          {hasQuickLinks && hasStandardActions ? (
            <div className="hidden h-4 w-px bg-border/80 sm:block mx-0.5 shrink-0" aria-hidden />
          ) : null}

          {/* Standard Actions (Inline + Menu) */}
          {inline.map((action) => (
            <ActionControl key={action.id} action={action} />
          ))}
          {menu.length > 0 ? (
            <DropdownMenu modal={false}>
              <WithTooltip label="Thêm thao tác">
                <DropdownMenuTrigger asChild>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    tooltip={false}
                    label="Thêm thao tác"
                    className="border-border/80 text-text-secondary hover:text-text hover:bg-bg-subtle shrink-0"
                    icon={<MoreHorizontal className="size-5" strokeWidth={2.2} />}
                    iconClassName="size-5 [&>svg]:size-5"
                  />
                </DropdownMenuTrigger>
              </WithTooltip>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="w-auto min-w-[11.5rem] max-w-[20rem] rounded-xl border border-border/80 bg-surface p-1.5 shadow-xl overflow-visible"
              >
                <DropdownMenuPrimitive.Arrow asChild width={12} height={6} className="-mt-[1px]">
                  <svg
                    width={12}
                    height={6}
                    viewBox="0 0 30 10"
                    preserveAspectRatio="none"
                    className="block overflow-visible"
                  >
                    <path
                      d="M 0 0 L 15 10 L 30 0"
                      className="fill-surface stroke-border stroke-[1.25]"
                    />
                  </svg>
                </DropdownMenuPrimitive.Arrow>
                {menu.map((action, index) => {
                  const icon = resolvePageActionIcon(
                    action,
                    cn(
                      "size-4 shrink-0",
                      action.danger ? "text-danger-fg" : "text-primary",
                    ),
                  );
                  const label = resolvePageActionLabel(action);
                  const title = resolvePageActionTitle(action);
                  const itemClass = cn(
                    "cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium rounded-lg transition-colors whitespace-nowrap",
                    "hover:bg-primary-soft/60 focus:bg-primary-soft/60 data-[highlighted]:bg-primary-soft/60 text-text",
                    action.danger &&
                      "text-danger-fg focus:bg-danger-soft focus:text-danger-fg hover:bg-danger-soft hover:text-danger-fg data-[highlighted]:bg-danger-soft data-[highlighted]:text-danger-fg",
                  );
                  const item = action.to && !action.disabled ? (
                    <DropdownMenuItem asChild className={itemClass}>
                      <Link
                        to={action.to}
                        aria-label={title}
                        className="flex items-center gap-2.5 w-full whitespace-nowrap text-inherit no-underline"
                      >
                        {icon}
                        <span className="whitespace-nowrap">{label}</span>
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      disabled={action.disabled}
                      className={itemClass}
                      onSelect={() => {
                        if (action.disabled) return;
                        // Defer so AlertDialog/Dialog can open after the menu
                        // unmounts (Radix focus/pointer-events cleanup).
                        window.setTimeout(() => {
                          action.onClick?.();
                        }, 0);
                      }}
                    >
                      {icon}
                      <span className="whitespace-nowrap">{label}</span>
                    </DropdownMenuItem>
                  );
                  return (
                    <Fragment key={action.id}>
                      {firstDangerIndex === index && firstDangerIndex > 0 ? (
                        <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-border/60" />
                      ) : null}
                      {item}
                    </Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}

      {showFab && createAction ? (
        <CreateFab action={createAction} clearBottomNav={clearBottomNav} />
      ) : null}
    </>
  );
}
